import { RouterProvider } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QueryClientProvider } from '@/lib/query';
import { ClerkProvider, SignedIn, SignedOut } from '@clerk/clerk-react';
import { queryClient } from '@/lib/query-client';
import { router } from '@/router';
import { ClerkTokenBridge } from '@/components/auth/ClerkTokenBridge';
import { ClerkAuthView } from '@/components/auth/ClerkAuthView';
import ImpersonateUser from '@/pages/ImpersonateUser';
import { resolveClerkLocalization } from '@/config/clerk-localizations';
import { TooltipProvider } from '@/shared/ui/tooltip';

const PUBLISHABLE_KEY: string = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error('Missing required environment variable: VITE_CLERK_PUBLISHABLE_KEY');
}

// Rendered outside SignedIn/SignedOut since the browser is signed out when
// it lands here — see ImpersonateUser. Shares the `/impersonateUser` path
// with the Admin-only panel (router/index.tsx): a `ticket` param means this
// is a ticket redemption, its absence means the normal panel should render.
const IMPERSONATE_PATH: string = '/impersonateUser';
const TICKET_PARAM: string = 'ticket';

export default function App(): React.JSX.Element {
  const { i18n } = useTranslation();

  const isRedeemTicketRoute: boolean =
    window.location.pathname === IMPERSONATE_PATH &&
    new URLSearchParams(window.location.search).has(TICKET_PARAM);

  if (isRedeemTicketRoute) {
    return (
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        afterSignOutUrl="/"
        localization={resolveClerkLocalization(i18n.language)}
      >
        <ImpersonateUser />
      </ClerkProvider>
    );
  }

  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      afterSignOutUrl="/"
      localization={resolveClerkLocalization(i18n.language)}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkTokenBridge />
        <SignedIn>
          <TooltipProvider>
            <RouterProvider router={router} />
          </TooltipProvider>
        </SignedIn>
        <SignedOut>
          <ClerkAuthView />
        </SignedOut>
      </QueryClientProvider>
    </ClerkProvider>
  );
}
